import { Info } from '@mui/icons-material'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { fullName } from '../../../../domain/Account/AccountDisplay'
import {
  getAdoptionAmountDisplay,
  getColorForAnimalAdoptionStatus,
  getColorForCannotBeAdoptedStatus,
  animalAdoptionOfferTypeOrder,
  getReadableAnimalAdoptionOfferType,
  getReadableAnimalAdoptionStatus,
  getReadableCannotBeAdoptedStatus,
} from '../../../../domain/Animal/AnimalDisplay'
import { AdoptersClient } from '../../../../interactors/clients/AdoptersClient'
import {
  AdopterDto,
  AdoptionAttemptDto,
  AdoptionAttemptDtoStatus,
  AnimalAdoptionDtoCannotBeAdoptedStatus,
  AnimalAdoptionDtoOfferType,
  AnimalAdoptionDtoStatus,
  AnimalAdoptionDtoStep,
  AnimalDto,
  AnimalDtoSpecies,
} from '../../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../../store/AccountsStore'
import { useAdoptersStore } from '../../../../store/AdoptersStore'
import { useGlobalSnackbarStore } from '../../../../store/GlobalSnackBarStore'
import { useEditAnimalMutation } from '../../../../store/useEditAnimalMutation'
import { useFetchOrRefreshAdoptersOnMount } from '../../../../store/useFetchOrRefreshAdoptersOnMount'
import { animalButtonShouldBeDisabled } from '../../../../utils/animalButtonShouldBeDisabled'
import { isDoneOutsidePetsoAttempt, isPetsoAttemptWithManualDocuments } from '../../../../utils/adoptionAttemptOrigin'
import { toDateOnly } from '../../../../utils/date/DateOnly'
import { isValidPastDateCoherence } from '../../../../utils/date/isValidPastDateCoherence'
import { useFormExtended } from '../../../../utils/hooks/useFormExtended'
import { useStripeConnectStatus } from '../../../../utils/hooks/useStripeConnectStatus'
import {
  AdoptionContext as FastLaunchAdoptionContext,
  FastLaunchProcedureDialog,
} from '../../../Adopters/FastLaunchProcedureDialog'
import { GuestAdoptionLinkDialog } from '../../../Adopters/GuestAdoption/GuestAdoptionLinkDialog'
import { GuestAdoptionLinkDto } from '../../../../interactors/clients/GuestAdoptionsClient'
import { createAdoptionAttempt } from '../../../../domain/Adopters/createAdoptionAttempt'
import { PATHS } from '../../../PATHS'
import { ColoredChipSelectField } from '../../../common/ColoredChipSelectField'
import { ControlledAutoCompleteWithCustomValue } from '../../../common/ControlledAutocompleteWithCustomValue'
import { ControlledDateField } from '../../../common/ControlledDateField'
import { ControlledSelectField } from '../../../common/ControlledSelectField'
import { getSoftChipSx } from '../../../common/chipStyles'
import { colors, spacingItem } from '../../../theme'
import { AnimalCardSaveButton } from '../AnimalCardSaveButton'
import { MainHostedFamilyFollowUp } from '../HostedFammilyFollowUpsTab/HostedFamillyFollowUpsTab'
import { CreateAdoptionAttemptConfirmationModal } from './CreateAdoptionAttemptConfirmationModal'
import { ForceAdopterAcceptedModal } from './ForceAdopterAcceptedModal'
import { GetAnimalAdoptedModal } from './GetAnimalAdoptedModal'
import { SuspendAttemptModal } from './SuspendAttemptModal'
import { VerdictAfterVPAModal } from './VerdictAfterVPAModal'

interface Props {
  animal: AnimalDto
}

type AnimalAdoptionLocationState = {
  fromAddAdopter?: {
    adopterId: string
    status?: AnimalAdoptionDtoStatus
    adoptionContext?: FastLaunchAdoptionContext
  }
}

export interface AdoptionFormParams {
  adoptionStatus: AnimalAdoptionDtoStatus
  adopterId: string | undefined
  cannotBeAdoptedStatus: AnimalAdoptionDtoCannotBeAdoptedStatus | ''
  exitDate: string | ''
  adoptionDate: string | ''
  futureAdoptionDate: string | ''
  vpaDate: string | ''
  step: AnimalAdoptionDtoStep | ''
  newName: string | ''
  goodbyeDate: string | ''
  goodbyeDetails: string | ''
  socialSecurityEntryDate: string | ''
  socialSecurityExitDate: string | ''
  priceInEuros: number | null
  offerType: AnimalAdoptionDtoOfferType | null
  gpsPoint?: string
  deceasedAfterAdoption?: boolean
  depositDate?: string
  depositAmount?: number
  depositStatus?: string
  hasDeposit: boolean
  paymentDone: string
}

type DepositStatusFieldValue = 'true' | 'false' | undefined

const toDepositStatusFieldValue = (value?: boolean | string | null): DepositStatusFieldValue => {
  if (value === true || value === 'true') {
    return 'true'
  }

  if (value === false || value === 'false') {
    return 'false'
  }

  return undefined
}

const toDepositStatusAttemptValue = (value?: string | boolean | null): boolean | undefined => {
  if (value === true || value === 'true') {
    return true
  }

  if (value === false || value === 'false') {
    return false
  }

  return undefined
}

const getRelevantAttemptForAnimal = (
  attempts: AdoptionAttemptDto[] | undefined,
  animalId: string,
  adoptionStatus: AnimalAdoptionDtoStatus | undefined
): AdoptionAttemptDto | undefined => {
  const animalAttempts = (attempts ?? []).filter((attempt) => attempt.animalId === animalId)
  const latestAnimalAttempts = [...animalAttempts].reverse()

  if (latestAnimalAttempts.length === 0) {
    return undefined
  }

  if (adoptionStatus === AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable) {
    return (
      latestAnimalAttempts.find(
        (attempt) =>
          attempt.done !== true &&
          attempt.archived !== true &&
          attempt.status !== AdoptionAttemptDtoStatus.SuspendedByCustomer &&
          attempt.status !== AdoptionAttemptDtoStatus.SuspendedByUser
      ) || latestAnimalAttempts[0]
    )
  }

  if (adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted) {
    return (
      latestAnimalAttempts.find(
        (attempt) =>
          attempt.done === true || attempt.archived === true || attempt.status === AdoptionAttemptDtoStatus.Done
      ) || latestAnimalAttempts[0]
    )
  }

  return latestAnimalAttempts[0]
}

export const AnimalAdoptionTab: React.FC<Props> = ({ animal }) => {
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const locationState = location.state as AnimalAdoptionLocationState | null
  const navigate = useNavigate()
  const adoptersStore = useAdoptersStore()
  const accountsStore = useAccountsStore()
  const globalSnackBarStore = useGlobalSnackbarStore()
  const { status: stripeStatus } = useStripeConnectStatus(true)
  const [isOpen, setIsOpen] = React.useState(false)
  const [adoptedModalIsOpen, setAdoptedModalIsOpen] = React.useState(false)
  const [suspendModalIsOpen, setSuspendModalIsOpen] = React.useState(false)
  const [getAnimalAdoptedIsOpen, setGetAnimalAdoptedIsOpen] = React.useState(false)
  const [verdictVPAModalIsOpen, setVerdictVPAModalIsOpen] = React.useState(false)
  const [alreadyExistingAttempt, setAlreadyExistingAttempt] = React.useState(false)
  const [isSameAdopter, setIsSameAdopter] = React.useState(false)
  const [oldAdopterName, setOldAdopterName] = React.useState<string | undefined>(undefined)
  const [adoptionPopUpOpen, setAdoptionPopUpOpen] = React.useState(false)
  const [guestAdoptionLink, setGuestAdoptionLink] = React.useState<GuestAdoptionLinkDto | null>(null)
  const [attemptId, setAttemptId] = React.useState<string | undefined>(undefined)
  const [dialogAttempt, setDialogAttempt] = React.useState<AdoptionAttemptDto | undefined>(undefined)
  const [dialogAdoptionContext, setDialogAdoptionContext] = React.useState<FastLaunchAdoptionContext | undefined>(
    undefined
  )
  const [isRestoringAdoptionFlow, setIsRestoringAdoptionFlow] = React.useState(false)
  const sendEmailToAdopter = true
  const [savedAdoptionAttemptContext, setSavedAdoptionAttemptContext] = React.useState<
    | {
        adopterId?: string
        attempt: AdoptionAttemptDto
      }
    | undefined
  >(undefined)

  const [depositInfoLoaded, setDepositInfoLoaded] = React.useState(false)

  const onClose = () => setIsOpen(false)
  const onCloseAdoptedModal = () => setAdoptedModalIsOpen(false)
  const onCloseSuspendModal = () => setSuspendModalIsOpen(false)
  const onCloseGetAnimalAdopted = () => setGetAnimalAdoptedIsOpen(false)
  const onCloseVerdictVPAModal = () => setVerdictVPAModalIsOpen(false)
  const rememberAdoptionAttempt = React.useCallback((adopterId: string | undefined, attempt: AdoptionAttemptDto) => {
    setSavedAdoptionAttemptContext({
      adopterId,
      attempt,
    })
  }, [])

  useEffect(() => {
    const openVPAModal = () => {
      if (
        animal.adoption?.vpaDate &&
        animal.adoption.status === AnimalAdoptionDtoStatus.HasBeenAdopted &&
        dayjs().isAfter(dayjs(animal.adoption.vpaDate)) &&
        animal.adoption.step !== AnimalAdoptionDtoStep.AdoptionValidatedAfterPostVisit
      ) {
        setVerdictVPAModalIsOpen(true)
      }
    }
    openVPAModal()
  }, [animal.adoption?.vpaDate])

  useFetchOrRefreshAdoptersOnMount()

  const initialAdopter = adoptersStore.adopters.find((adopter) => adopter.id === animal.adoption?.adopterId)

  const adoptionAttempt = initialAdopter?.adoptionAttempts?.find(
    (adoptionAttempt) => adoptionAttempt.animalId === animal.id
  )

  const options = adoptersStore.adopters.map((adopter) => {
    const label = adopter.isDeleted ? `${fullName(adopter)} (Supprimé)` : fullName(adopter)

    return { ...adopter, label, key: adopter.id }
  })

  const initialAdopterOption = options.find((adopter) => adopter.id === animal.adoption?.adopterId)

  const getDefaultValues = (animal: AnimalDto): AdoptionFormParams => {
    const initialAdopterOption = options.find((adopter) => adopter.id === animal.adoption?.adopterId)

    return {
      adoptionStatus: animal.adoption.status || '',
      adopterId: initialAdopterOption?.id || undefined,
      cannotBeAdoptedStatus: animal.adoption.cannotBeAdoptedStatus || '',
      exitDate: animal.adoption.exitDate || '',
      adoptionDate: animal.adoption.adoptionDate || '',
      futureAdoptionDate: animal.adoption.futureAdoptionDate || '',
      vpaDate: animal.adoption.vpaDate || '',
      step: animal.adoption.step || '',
      newName: animal.adoption.newName || '',
      goodbyeDate: animal.adoption.goodbyeDate || '',
      goodbyeDetails: animal.adoption.goodbyeDetails || '',
      socialSecurityEntryDate: animal.adoption.socialSecurityEntryDate || '',
      socialSecurityExitDate: animal.adoption.socialSecurityExitDate || '',
      priceInEuros: animal.adoption.priceInEuros || null,
      offerType: animal.adoption.offerType || null,
      gpsPoint: animal.gpsPoint,
      deceasedAfterAdoption: animal.adoption.deceasedAfterAdoption,
      depositDate: savedAdoptionAttemptContext?.attempt.depositDate || undefined,
      depositAmount: savedAdoptionAttemptContext?.attempt.depositAmount || undefined,
      depositStatus: toDepositStatusFieldValue(savedAdoptionAttemptContext?.attempt.depositStatus),
      hasDeposit: Boolean(
        savedAdoptionAttemptContext?.attempt.depositDate || savedAdoptionAttemptContext?.attempt.depositAmount
      ),
      paymentDone: savedAdoptionAttemptContext?.attempt.paymentDone ? 'true' : 'false',
    }
  }

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
    register,
    setValue,
    getValues,
  } = useFormExtended(animal, getDefaultValues)

  const depositAmount = watch('depositAmount')
  const depositDate = watch('depositDate')
  const depositStatus = watch('depositStatus')
  const hasDeposit = watch('hasDeposit')

  useEffect(() => {
    if (!adoptionPopUpOpen) {
      setDialogAdoptionContext(undefined)
    }
  }, [adoptionPopUpOpen])

  useEffect(() => {
    reset(getDefaultValues(animal))
  }, [initialAdopterOption?.id])

  const isDonation = watch('offerType') === AnimalAdoptionDtoOfferType.Donation
  const isFreeAmount = watch('offerType') === AnimalAdoptionDtoOfferType.FreeAmount
  useEffect(() => {
    if (isDonation) {
      setValue('priceInEuros', 0, { shouldValidate: true })
    }
  }, [isDonation, setValue])

  const watchAdopter = watch('adopterId')
  const watchAdoptionStatus = watch('adoptionStatus')
  const watchCannotBeAdoptedStatus = watch('cannotBeAdoptedStatus')
  const watchedOfferType = watch('offerType')
  const watchedPriceInEuros = watch('priceInEuros')
  useEffect(() => {
    if (
      isFreeAmount &&
      (watchedPriceInEuros === undefined || watchedPriceInEuros === null || Number(watchedPriceInEuros) < 1)
    ) {
      setValue('priceInEuros', 1, { shouldValidate: true })
    }
  }, [isFreeAmount, setValue, watchedPriceInEuros])
  const cannotBeAdopted = watchAdoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted
  const isAdoptedStatus = watchAdoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted
  const currentAdopter =
    (watchAdopter ? adoptersStore.adopters.find((adopter) => adopter.id === watchAdopter) : undefined) || null
  const currentAdoptionAttempt =
    (savedAdoptionAttemptContext?.adopterId != null &&
    savedAdoptionAttemptContext.adopterId === watchAdopter &&
    savedAdoptionAttemptContext.attempt.animalId === animal.id
      ? savedAdoptionAttemptContext.attempt
      : undefined) ||
    getRelevantAttemptForAnimal(currentAdopter?.adoptionAttempts, animal.id, watchAdoptionStatus) ||
    (initialAdopter?.id === watchAdopter && adoptionAttempt?.animalId === animal.id ? adoptionAttempt : undefined)

  useEffect(() => {
    setDepositInfoLoaded(false)
  }, [watchAdopter, currentAdoptionAttempt?.id])

  useEffect(() => {
    if (
      !depositInfoLoaded &&
      currentAdoptionAttempt &&
      depositAmount === undefined &&
      depositDate === undefined &&
      depositStatus === undefined
    ) {
      setValue('depositAmount', currentAdoptionAttempt?.depositAmount || undefined)
      setValue('depositDate', currentAdoptionAttempt?.depositDate || undefined)
      setValue('depositStatus', toDepositStatusFieldValue(currentAdoptionAttempt?.depositStatus))
      setValue('hasDeposit', Boolean(currentAdoptionAttempt?.depositDate || currentAdoptionAttempt?.depositAmount))
      setValue('paymentDone', currentAdoptionAttempt?.paymentDone ? 'true' : 'false')

      setDepositInfoLoaded(true)
    }
  }, [currentAdoptionAttempt, depositAmount, depositDate, depositInfoLoaded, depositStatus, setValue])

  const hasExitDates =
    (
      [AnimalAdoptionDtoStatus.HasBeenAdopted, AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable] as Array<string>
    ).includes(watchAdoptionStatus) ||
    // Or FALD
    (watchAdoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted &&
      watchCannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily)

  const hasGoodbyeDates =
    watchAdoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted &&
    (
      [
        AnimalAdoptionDtoCannotBeAdoptedStatus.Dead,
        AnimalAdoptionDtoCannotBeAdoptedStatus.Lost,
        AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation,
        AnimalAdoptionDtoCannotBeAdoptedStatus.Released,
      ] as Array<string>
    ).includes(watchCannotBeAdoptedStatus)

  const hasSocialSecurityDates =
    watchAdoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted &&
    watchCannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.SocialSecurity

  const newAdoptionStep = (data: AdoptionFormParams) => {
    if (data.adoptionStatus === AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable) {
      return AnimalAdoptionDtoStep.AdoptionToBeValidated
    }
    if (data.step === AnimalAdoptionDtoStep.AdoptionValidatedAfterPostVisit) {
      return AnimalAdoptionDtoStep.AdoptionValidatedAfterPostVisit
    }
    if (data.adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted) {
      return AnimalAdoptionDtoStep.AdoptionValidatedWithoutPostVisit
    }

    return undefined
  }

  const editAnimalMutation = useEditAnimalMutation({})

  const onSubmit = async (
    data: AdoptionFormParams,
    confirmation?: boolean,
    nextDialogAdoptionContext?: FastLaunchAdoptionContext
  ) => {
    let shouldKeepProcedureDialogOpen = false
    let shouldOpenProcedureDialogAfterSave = false

    if (isSameAdopter) {
      setIsSameAdopter(false)
    }
    if (alreadyExistingAttempt) {
      setAlreadyExistingAttempt(false)
    }
    if (oldAdopterName) {
      setOldAdopterName(undefined)
    }

    if (isOpen) {
      onClose()
    }
    if (adoptedModalIsOpen) {
      onCloseAdoptedModal()
    }
    if (suspendModalIsOpen) {
      onCloseSuspendModal()
    }
    if (getAnimalAdoptedIsOpen) {
      onCloseGetAnimalAdopted()
    }

    if (data.adopterId) {
      const adopter = adoptersStore.adopters.find((adopter) => adopter.id === data.adopterId)
      if (adopter && adopter.isDeleted) {
        return globalSnackBarStore.triggerErrorMessage('Vous ne pouvez pas assigner un adoptant supprimé')
      }
    }
    if (data.adoptionDate && !animal.adoption.adoptionDate) {
      data.adoptionStatus = AnimalAdoptionDtoStatus.HasBeenAdopted
      if (!data.adopterId && !confirmation) {
        return setGetAnimalAdoptedIsOpen(true)
      }
    }

    let adopterThatAlreadyHasAttempt: AdopterDto | null = null
    if (animal.adoption.adopterId != null) {
      const adopter = adoptersStore.adopters.find((adopter) => adopter.id === animal.adoption.adopterId)
      const existingAttempt = getRelevantAttemptForAnimal(adopter?.adoptionAttempts, animal.id, animal.adoption.status)
      if (adopter && existingAttempt) {
        adopterThatAlreadyHasAttempt = adopter
        if (adopterThatAlreadyHasAttempt.phoneNumber?.startsWith('0')) {
          adopterThatAlreadyHasAttempt.phoneNumber = `+33${adopterThatAlreadyHasAttempt.phoneNumber.slice(1)}`
        }
        setAlreadyExistingAttempt(true)
        setOldAdopterName(fullName(adopter))
        setIsSameAdopter(adopter.id === data.adopterId)
      }
    }

    let newFollowUp: MainHostedFamilyFollowUp | undefined = undefined
    if (
      animalButtonShouldBeDisabled({
        ...animal,
        adoption: {
          ...animal.adoption,
          status: data.adoptionStatus as AnimalAdoptionDtoStatus,
          adopterId: data.adoptionStatus === AnimalAdoptionDtoStatus.Adoptable ? undefined : data.adopterId,
          exitDate: data.exitDate || undefined,
          adoptionDate: data.adoptionDate ? toDateOnly(data.adoptionDate) : undefined,
          futureAdoptionDate: data.futureAdoptionDate ?? undefined,
          cannotBeAdoptedStatus: (data.cannotBeAdoptedStatus as AnimalAdoptionDtoCannotBeAdoptedStatus) || undefined,
          step: (data.step as AnimalAdoptionDtoStep) || undefined,
          goodbyeDate: hasGoodbyeDates && data.goodbyeDate ? data.goodbyeDate : undefined,
          goodbyeDetails: hasGoodbyeDates && data.goodbyeDetails ? toDateOnly(data.goodbyeDetails) : undefined,
          offerType: data.offerType ? data.offerType : undefined,
          priceInEuros: data.priceInEuros ? data.priceInEuros : undefined,
        },
      })
    ) {
      if (data.adoptionStatus === 'cannot-be-adopted' && data.goodbyeDate) {
        newFollowUp = {
          type: 'exit',
          date: toDateOnly(dayjs(data.goodbyeDate)),
          destination: `${data.cannotBeAdoptedStatus}`,
          memberInChargeAccountId:
            accountsStore.connectedAccount?.id || animal.responsibilities?.memberInChargeAccountId || '',
          created: {
            by: accountsStore.connectedAccount?.id || '',
            at: dayjs().toISOString(),
          },
        }
      }
      if (data.adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted && data.exitDate && data.adopterId) {
        newFollowUp = {
          type: 'exit',
          date: toDateOnly(dayjs(data.exitDate)),
          destination: `${data.adoptionStatus}`,
          memberInChargeAccountId:
            accountsStore.connectedAccount?.id || animal.responsibilities?.memberInChargeAccountId || '',
          created: {
            by: accountsStore.connectedAccount?.id || '',
            at: dayjs().toISOString(),
          },
        }
      }
    }

    let nextAdoptionAttemptAfterSave: AdoptionAttemptDto | undefined = undefined

    if (
      data.adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted &&
      data.adopterId &&
      (animal.adoption.status !== AnimalAdoptionDtoStatus.HasBeenAdopted ||
        animal.adoption.adopterId !== data.adopterId)
    ) {
      const adopter = adoptersStore.adopters.find((adopter) => adopter.id === data.adopterId)
      const isSwitchingToAnotherAdopter =
        adopter != null && adopterThatAlreadyHasAttempt != null && adopter.id !== adopterThatAlreadyHasAttempt.id
      const targetAdopterAttempt = getRelevantAttemptForAnimal(
        adopter?.adoptionAttempts,
        animal.id,
        AnimalAdoptionDtoStatus.HasBeenAdopted
      )
      if (confirmation || (!alreadyExistingAttempt && adopter?.profileHasBeenAccepted === 'accepted')) {
        if (adopterThatAlreadyHasAttempt) {
          const index = adopterThatAlreadyHasAttempt.adoptionAttempts?.findIndex(
            (attempt) => attempt.animalId === animal.id
          )
          if (index !== -1 && index != null && adopterThatAlreadyHasAttempt.adoptionAttempts) {
            if (adopter && adopter.id === adopterThatAlreadyHasAttempt.id) {
              adopterThatAlreadyHasAttempt.adoptionAttempts[index].status = AdoptionAttemptDtoStatus.Done
              adopterThatAlreadyHasAttempt.adoptionAttempts[index].step = 6
              adopterThatAlreadyHasAttempt.adoptionAttempts[index].done = true
              adopterThatAlreadyHasAttempt.adoptionAttempts[index].depositDate = data.depositDate
              adopterThatAlreadyHasAttempt.adoptionAttempts[index].depositAmount = data.depositAmount
              adopterThatAlreadyHasAttempt.adoptionAttempts[index].depositStatus = toDepositStatusAttemptValue(
                data.depositStatus
              )
              nextAdoptionAttemptAfterSave = adopterThatAlreadyHasAttempt.adoptionAttempts[index]
              rememberAdoptionAttempt(data.adopterId, adopterThatAlreadyHasAttempt.adoptionAttempts[index])
            } else {
              adopterThatAlreadyHasAttempt.adoptionAttempts[index].status = AdoptionAttemptDtoStatus.SuspendedByCustomer
            }
            adopterThatAlreadyHasAttempt.phoneNumber = ['+33', ''].includes(
              adopterThatAlreadyHasAttempt.phoneNumber?.trim() || ''
            )
              ? undefined
              : adopterThatAlreadyHasAttempt.phoneNumber
            await AdoptersClient.editAccount(adopterThatAlreadyHasAttempt)
          }
        }
        if (adopter && targetAdopterAttempt && adopter.id !== adopterThatAlreadyHasAttempt?.id) {
          const index = adopter.adoptionAttempts?.findIndex((attempt) => attempt.animalId === animal.id)
          if (index !== -1 && index != null && adopter.adoptionAttempts) {
            adopter.adoptionAttempts[index].status = AdoptionAttemptDtoStatus.Done
            adopter.adoptionAttempts[index].step = 6
            adopter.adoptionAttempts[index].done = true
            adopter.adoptionAttempts[index].procedureOrigin = 'outside-petso'
            adopter.adoptionAttempts[index].withoutDocumentsSupport = true
            nextAdoptionAttemptAfterSave = adopter.adoptionAttempts[index]
            rememberAdoptionAttempt(data.adopterId, adopter.adoptionAttempts[index])
            adopter.phoneNumber = ['+33', ''].includes(adopter.phoneNumber?.trim() || '')
              ? undefined
              : adopter.phoneNumber
            await AdoptersClient.editAccount(adopter)
          }
        }
        if (adopter && adopter.profileHasBeenAccepted !== 'accepted') {
          adopter.profileHasBeenAccepted = 'accepted'
          if (adopter.phoneNumber?.startsWith('0')) {
            adopter.phoneNumber = `+33${adopter.phoneNumber.slice(1)}`
          }
          adopter.phoneNumber = ['+33', ''].includes(adopter.phoneNumber?.trim() || '')
            ? undefined
            : adopter.phoneNumber
          await AdoptersClient.editAccount(adopter)
        }
      } else {
        return setAdoptedModalIsOpen(true)
      }

      if (
        adopter &&
        (!targetAdopterAttempt || isSwitchingToAnotherAdopter) &&
        (!targetAdopterAttempt || targetAdopterAttempt.procedureOrigin !== 'outside-petso') &&
        !adopter.adoptionAttempts?.some(
          (adoptionAttempt) =>
            adoptionAttempt.animalId === animal.id &&
            (!isSwitchingToAnotherAdopter || adoptionAttempt.id === targetAdopterAttempt?.id)
        )
      ) {
        const newAttemptId = await AdoptersClient.createDoneAdoptionAttempt(adopter.id, animal.id, false)
        const optimisticPastAttempt = {
          id: newAttemptId,
          animalId: animal.id,
          status: AdoptionAttemptDtoStatus.Done,
          step: 6,
          done: true,
          procedureOrigin: 'outside-petso',
          withoutDocumentsSupport: true,
          depositDate: data.depositDate,
          depositAmount: data.depositAmount,
          depositStatus: toDepositStatusAttemptValue(data.depositStatus),
          paymentDone: false,
        } as AdoptionAttemptDto
        nextAdoptionAttemptAfterSave = optimisticPastAttempt
        rememberAdoptionAttempt(data.adopterId, optimisticPastAttempt)
      }
    }

    if (
      data.adoptionStatus === AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable &&
      data.adopterId &&
      (animal.adoption.status !== AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable ||
        animal.adoption.adopterId !== data.adopterId)
    ) {
      if (confirmation) {
        if (adopterThatAlreadyHasAttempt) {
          const index = adopterThatAlreadyHasAttempt.adoptionAttempts?.findIndex(
            (attempt) => attempt.animalId === animal.id
          )
          if (index !== -1 && index != null && adopterThatAlreadyHasAttempt.adoptionAttempts) {
            adopterThatAlreadyHasAttempt.adoptionAttempts[index].status = AdoptionAttemptDtoStatus.SuspendedByCustomer
            adopterThatAlreadyHasAttempt.phoneNumber = ['+33', ''].includes(
              adopterThatAlreadyHasAttempt.phoneNumber?.trim() || ''
            )
              ? undefined
              : adopterThatAlreadyHasAttempt.phoneNumber
            await AdoptersClient.editAccount(adopterThatAlreadyHasAttempt)
          }
        }

        const adopter = adoptersStore.adopters.find((adopter) => adopter.id === data.adopterId)
        const targetAdopterAttempt = getRelevantAttemptForAnimal(
          adopter?.adoptionAttempts,
          animal.id,
          AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable
        )

        if (
          !targetAdopterAttempt ||
          targetAdopterAttempt.done === true ||
          targetAdopterAttempt.archived === true ||
          targetAdopterAttempt.status === AdoptionAttemptDtoStatus.SuspendedByCustomer ||
          targetAdopterAttempt.status === AdoptionAttemptDtoStatus.SuspendedByUser
        ) {
          // Without a context nobody has said yet whether Petso prepares the documents, and that is
          // what decides between a payment link and the usual procedure. Creating the attempt here
          // would settle it before the question is asked, so the dialog opens and does it at its
          // first step instead. With a context -- coming back from the adopter creation screen --
          // the answer is already known and the attempt can be created straight away.
          if (stripeStatus !== 'connected' || !nextDialogAdoptionContext) {
            setDialogAdoptionContext(nextDialogAdoptionContext)
            setAttemptId(undefined)
            setDialogAttempt(undefined)
            setAdoptionPopUpOpen(true)
            return
          }
          const result = await createAdoptionAttempt({
            adopterId: data.adopterId,
            animalId: animal.id,
            shouldPrepareDocuments: nextDialogAdoptionContext.prepareDocuments ?? isPremium,
            sendEmailToAdopter,
            animal: {
              adoption: {
                ...animal.adoption,
                // The price just entered on the form wins, then the animal's own.
                priceInEuros:
                  nextDialogAdoptionContext.priceInEuros ?? data.priceInEuros ?? animal.adoption.priceInEuros,
              },
            },
            isStripeConnectReady: stripeStatus === 'connected',
            hasDeposit: nextDialogAdoptionContext.hasDeposit,
            depositAmount: nextDialogAdoptionContext.depositAmount,
            depositDate: nextDialogAdoptionContext.depositDate,
            depositStatus: nextDialogAdoptionContext.depositStatus,
          })
          const attempt = result.attempt
          nextAdoptionAttemptAfterSave = attempt
          rememberAdoptionAttempt(data.adopterId, attempt)
          setDialogAdoptionContext(nextDialogAdoptionContext)
          setAttemptId(attempt.id)
          setDialogAttempt(attempt)
          // A guest adoption is settled from the payment link, so the procedure dialog gives way to it.
          if (result.guestLink) {
            setGuestAdoptionLink(result.guestLink)
          } else {
            shouldKeepProcedureDialogOpen = true
            shouldOpenProcedureDialogAfterSave = true
          }
          /* if (adopter && adopter?.profileHasBeenAccepted !== 'accepted') {
            adopter.profileHasBeenAccepted = 'accepted'
            if (adopter.phoneNumber?.startsWith('0')) {
              adopter.phoneNumber = `+33${adopter.phoneNumber.slice(1)}`
            }
            adopter.phoneNumber = ['+33', ''].includes(adopter.phoneNumber?.trim() || '')
              ? undefined
              : adopter.phoneNumber
            await AdoptersClient.editAccount(adopter)
          } */
        }
      } else {
        return setIsOpen(true)
      }
    }

    if (
      adopterThatAlreadyHasAttempt != null &&
      ((data.adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted && !data.adopterId) ||
        data.adoptionStatus === AnimalAdoptionDtoStatus.Adoptable ||
        data.adoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted)
    ) {
      if (confirmation) {
        if (adopterThatAlreadyHasAttempt) {
          const index = adopterThatAlreadyHasAttempt.adoptionAttempts?.findIndex(
            (attempt) => attempt.animalId === animal.id
          )
          if (index !== -1 && index != null && adopterThatAlreadyHasAttempt.adoptionAttempts) {
            adopterThatAlreadyHasAttempt.adoptionAttempts[index].status = AdoptionAttemptDtoStatus.SuspendedByCustomer
            adopterThatAlreadyHasAttempt.phoneNumber = ['+33', ''].includes(
              adopterThatAlreadyHasAttempt.phoneNumber?.trim() || ''
            )
              ? undefined
              : adopterThatAlreadyHasAttempt.phoneNumber
            await AdoptersClient.editAccount(adopterThatAlreadyHasAttempt)
          }
        }
      } else {
        return setSuspendModalIsOpen(true)
      }
    }
    if (currentAdoptionAttempt && data.adopterId && currentAdopter && currentAdopter.id === data.adopterId) {
      const depositDateChanged = !dayjs(data.depositDate).isSame(
        dayjs(currentAdoptionAttempt.depositDate || undefined),
        'day'
      )
      const depositAmountChanged =
        data.depositAmount?.toString() !== '' && data.depositAmount !== currentAdoptionAttempt.depositAmount
      const depositStatusChanged =
        toDepositStatusAttemptValue(data.depositStatus) !== currentAdoptionAttempt.depositStatus

      if (depositDateChanged || depositAmountChanged || depositStatusChanged) {
        const updatedAttempt = await AdoptersClient.updateAdoptionAttempt(data.adopterId, {
          ...currentAdoptionAttempt,
          depositDate: data.depositDate,
          depositAmount: data.depositAmount,
          depositStatus: toDepositStatusAttemptValue(data.depositStatus),
        })
        nextAdoptionAttemptAfterSave = updatedAttempt
        rememberAdoptionAttempt(data.adopterId, updatedAttempt)
      }
    }

    const canHaveNewName =
      (watchAdoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted &&
        watchCannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily) ||
      watchAdoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted

    const updatedAnimal = await editAnimalMutation.mutateAsync({
      ...animal,
      hostedFamilyFollowUps: {
        ...animal.hostedFamilyFollowUps,
        main: [...(animal.hostedFamilyFollowUps.main || []), newFollowUp].filter(
          (followUp) => followUp
        ) as MainHostedFamilyFollowUp[],
      },
      gpsPoint: data.gpsPoint,
      adoption: {
        ...animal.adoption,
        status: data.adoptionStatus as AnimalAdoptionDtoStatus,
        adopterId:
          data.adoptionStatus !== AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable &&
          data.adoptionStatus !== AnimalAdoptionDtoStatus.HasBeenAdopted
            ? undefined
            : data.adopterId,
        exitDate:
          data.adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted ||
          data.adoptionStatus === AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable
            ? data.exitDate
            : undefined,
        adoptionDate:
          (data.adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted ||
            data.adoptionStatus === AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable) &&
          data.adoptionDate
            ? toDateOnly(data.adoptionDate)
            : undefined,
        futureAdoptionDate:
          data.adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted ||
          data.adoptionStatus === AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable
            ? data.futureAdoptionDate
            : undefined,
        vpaDate: data.adoptionStatus !== AnimalAdoptionDtoStatus.HasBeenAdopted ? undefined : data.vpaDate,
        cannotBeAdoptedStatus: (data.cannotBeAdoptedStatus as AnimalAdoptionDtoCannotBeAdoptedStatus) || undefined,
        step: newAdoptionStep(data) ?? ((data.step as AnimalAdoptionDtoStep) || undefined),
        newName: data.newName && canHaveNewName ? data.newName : undefined,
        goodbyeDate:
          hasGoodbyeDates && data.goodbyeDate && data.adoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted
            ? data.goodbyeDate
            : undefined,
        goodbyeDetails:
          hasGoodbyeDates && data.adoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted && data.goodbyeDetails
            ? toDateOnly(data.goodbyeDetails)
            : undefined,
        socialSecurityEntryDate:
          hasSocialSecurityDates && data.socialSecurityEntryDate ? data.socialSecurityEntryDate : undefined,
        socialSecurityExitDate:
          hasSocialSecurityDates && data.socialSecurityExitDate ? data.socialSecurityExitDate : undefined,
        offerType: data.offerType ? data.offerType : undefined,
        priceInEuros:
          data.offerType === AnimalAdoptionDtoOfferType.Donation
            ? 0
            : data.priceInEuros
            ? data.priceInEuros
            : undefined,
        deceasedAfterAdoption:
          data.adoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted ? data.deceasedAfterAdoption : undefined,
      },
    })
    await adoptersStore.fetchOrRefreshAdopters()

    const refreshedCurrentAdopter =
      updatedAnimal.adoption.adopterId != null
        ? adoptersStore.adopters.find((adopter) => adopter.id === updatedAnimal.adoption.adopterId)
        : undefined

    const refreshedCurrentAttempt =
      nextAdoptionAttemptAfterSave ||
      getRelevantAttemptForAnimal(
        refreshedCurrentAdopter?.adoptionAttempts,
        updatedAnimal.id,
        updatedAnimal.adoption.status
      )

    if (refreshedCurrentAttempt) {
      rememberAdoptionAttempt(updatedAnimal.adoption.adopterId || undefined, refreshedCurrentAttempt)
    } else {
      setSavedAdoptionAttemptContext(undefined)
    }

    reset({
      ...getDefaultValues(updatedAnimal),
      adopterId: updatedAnimal.adoption.adopterId || undefined,
      depositDate: refreshedCurrentAttempt?.depositDate || undefined,
      depositAmount: refreshedCurrentAttempt?.depositAmount || undefined,
      depositStatus: toDepositStatusFieldValue(refreshedCurrentAttempt?.depositStatus),
      hasDeposit: Boolean(refreshedCurrentAttempt?.depositDate || refreshedCurrentAttempt?.depositAmount),
      paymentDone: refreshedCurrentAttempt?.paymentDone ? 'true' : 'false',
    })
    setDepositInfoLoaded(Boolean(refreshedCurrentAttempt))
    if (shouldOpenProcedureDialogAfterSave) {
      setAdoptionPopUpOpen(true)
    } else if (!shouldKeepProcedureDialogOpen) {
      setAdoptionPopUpOpen(false)
      setAttemptId(undefined)
      setDialogAttempt(undefined)
      setDialogAdoptionContext(undefined)
    }
    setAlreadyExistingAttempt(false)
    setIsSameAdopter(false)
    setOldAdopterName(undefined)
  }

  useEffect(() => {
    const returnedFromAddAdopter = locationState?.fromAddAdopter
    const isAnimalAdoptionReturn =
      queryParams.get('tab') === 'adoption' && queryParams.get('card') === 'animalAdoptionCard'

    if (!returnedFromAddAdopter && !isAnimalAdoptionReturn) {
      return
    }

    const newAdopter = returnedFromAddAdopter?.adopterId ?? queryParams.get('adoptant')
    const status = returnedFromAddAdopter?.status ?? queryParams.get('status')
    const returnedAdoptionContext = returnedFromAddAdopter?.adoptionContext
    const offerType = returnedAdoptionContext?.offerType ?? queryParams.get('offerType')
    const priceInEuros = returnedAdoptionContext?.priceInEuros ?? queryParams.get('priceInEuros')

    if (newAdopter !== undefined && newAdopter !== null && status !== undefined && status !== null) {
      ;(async () => {
        setIsRestoringAdoptionFlow(true)
        try {
          await adoptersStore.fetchOrRefreshAdopters()
          setValue('adopterId', newAdopter || '')
          setValue('adoptionStatus', status as AnimalAdoptionDtoStatus)
          if (offerType) {
            setValue('offerType', offerType as AnimalAdoptionDtoOfferType)
          }
          if (priceInEuros != null && priceInEuros !== '') {
            setValue('priceInEuros', Number(priceInEuros))
          }
          if (returnedAdoptionContext) {
            const hasReturnedDeposit = Boolean(returnedAdoptionContext.hasDeposit)
            setDialogAdoptionContext(returnedAdoptionContext)
            setValue('hasDeposit', hasReturnedDeposit)
            setValue('depositDate', hasReturnedDeposit ? returnedAdoptionContext.depositDate || undefined : undefined)
            setValue('depositAmount', hasReturnedDeposit ? returnedAdoptionContext.depositAmount : undefined)
            setValue(
              'depositStatus',
              hasReturnedDeposit ? toDepositStatusFieldValue(returnedAdoptionContext.depositStatus) : undefined
            )
          }
          const restoredFormData: AdoptionFormParams = {
            ...getValues(),
            adopterId: newAdopter || undefined,
            adoptionStatus: status as AnimalAdoptionDtoStatus,
          }
          await onSubmit(restoredFormData, true, returnedAdoptionContext)

          const cleanedQueryParams = new URLSearchParams(location.search)
          cleanedQueryParams.delete('adoptant')
          cleanedQueryParams.delete('card')
          cleanedQueryParams.delete('status')
          cleanedQueryParams.delete('offerType')
          cleanedQueryParams.delete('priceInEuros')
          const cleanedSearch = cleanedQueryParams.toString()
          navigate(`${location.pathname}${cleanedSearch ? `?${cleanedSearch}` : ''}`, {
            replace: true,
            state: null,
          })
        } catch (error) {
          console.error({ error })
          globalSnackBarStore.triggerErrorMessage(
            "Une erreur est survenue lors de la reprise de la procédure d'adoption."
          )
        } finally {
          setIsRestoringAdoptionFlow(false)
        }
      })()
    }
  }, [])

  useEffect(() => {
    if (watchAdopter === 'addAccount') {
      reset({ adopterId: undefined })
      navigate(
        `${PATHS.ajouterAdoptant.absolute}?from=animaux/${
          animal.id
        }?tab=adoption&card=animalAdoptionCard&status=${watchAdoptionStatus}&adoptionType=${
          watchAdoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted ? 'past' : 'request'
        }${watchedOfferType ? `&offerType=${watchedOfferType}` : ''}${
          watchedPriceInEuros != null ? `&priceInEuros=${watchedPriceInEuros}` : ''
        }`,
        {
          state: {
            fromAnimalAdoption: {
              animalId: animal.id,
              status: watchAdoptionStatus,
              adoptionType: watchAdoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted ? 'past' : 'request',
              offerType: watchedOfferType ?? undefined,
              priceInEuros: watchedPriceInEuros ?? undefined,
              hasDeposit,
              depositDate: hasDeposit ? depositDate : undefined,
              depositAmount: hasDeposit ? depositAmount : undefined,
              depositStatus: hasDeposit ? depositStatus : undefined,
            },
          },
        }
      )
    }
  }, [
    animal.id,
    depositAmount,
    depositDate,
    depositStatus,
    hasDeposit,
    navigate,
    reset,
    watchAdopter,
    watchAdoptionStatus,
    watchedOfferType,
    watchedPriceInEuros,
  ])

  const paymentDone = currentAdoptionAttempt?.paymentDone === true
  const paymentAmountInEuros = currentAdoptionAttempt?.paymentAmountInEuros
  const adoptionPrice = watch('priceInEuros')
  const normalizedAdoptionPrice =
    adoptionPrice == null || Number.isNaN(Number(adoptionPrice)) ? undefined : Number(adoptionPrice)
  const effectiveMinimumAmount = isFreeAmount
    ? Math.max(normalizedAdoptionPrice ?? 1, depositStatus === 'true' ? depositAmount ?? 0 : 0)
    : normalizedAdoptionPrice
  const latestPaymentReceptionEvent = currentAdoptionAttempt?.events
    ?.filter((event) => event.type === 'payment-reception')
    .sort((a, b) => {
      const dateA = dayjs(a.date, a.date?.includes('/') ? 'DD/MM/YYYY' : undefined)
      const dateB = dayjs(b.date, b.date?.includes('/') ? 'DD/MM/YYYY' : undefined)
      if (dateA.isAfter(dateB)) return -1
      if (dateA.isBefore(dateB)) return 1
      return 0
    })?.[0]

  const paymentDateDisplay =
    paymentDone && latestPaymentReceptionEvent?.date
      ? dayjs(
          latestPaymentReceptionEvent.date,
          latestPaymentReceptionEvent.date.includes('/') ? 'DD/MM/YYYY' : undefined
        ).format('DD/MM/YYYY')
      : ''

  const leftToPay =
    paymentDone === true
      ? 0
      : depositAmount && depositStatus === 'true' && effectiveMinimumAmount != null
      ? Math.max(effectiveMinimumAmount - depositAmount, 0)
      : effectiveMinimumAmount
  const hiddenDepositExists =
    typeof effectiveMinimumAmount === 'number' && typeof leftToPay === 'number' && leftToPay < effectiveMinimumAmount

  const fullPaymentCanBeHandledInProcedure =
    typeof effectiveMinimumAmount === 'number' &&
    typeof leftToPay === 'number' &&
    leftToPay === effectiveMinimumAmount &&
    Boolean(currentAdoptionAttempt && watchAdopter)

  const adoptionAttemptTab =
    currentAdoptionAttempt?.done === true ||
    currentAdoptionAttempt?.archived === true ||
    currentAdoptionAttempt?.status === 'done'
      ? 'past-adoption'
      : currentAdoptionAttempt?.status === 'suspended-by-customer' ||
        currentAdoptionAttempt?.status === 'suspended-by-user'
      ? 'suspended-adoption'
      : 'ongoing-adoption'

  const adoptionDate = watch('adoptionDate')
  const exitDatesDisabled = adoptionDate === '' || adoptionDate == null

  const selectedAdopter = currentAdopter
  const isPremium = accountsStore.isPremium()
  const hasLinkedAdoptionProcedure = Boolean(currentAdoptionAttempt && watchAdopter)
  const isPastAdoptionOutsidePetso = isDoneOutsidePetsoAttempt(currentAdoptionAttempt)
  const isPetsoProcedureWithManualDocuments = isPetsoAttemptWithManualDocuments(currentAdoptionAttempt)
  const isFerretAlreadySterilized =
    animal?.species === AnimalDtoSpecies.Ferret &&
    (animal?.health?.sterilizedForFerrets === 'sterilized' || animal?.health?.sterilizedForFerrets === 'implanted')
  const isAlreadySterilizedByAnimal = animal?.health?.sterilizedOrNeutered === 'true' || isFerretAlreadySterilized

  const certificateSummary = (() => {
    if (isPastAdoptionOutsidePetso) {
      return !currentAdoptionAttempt?.certificate?.original && !currentAdoptionAttempt?.certificate?.signed
        ? { label: 'À compléter', color: '#F8E9C7' }
        : { label: 'Reçu - hors Petso', color: '#DDEFE5' }
    }
    if (isPetsoProcedureWithManualDocuments) {
      return !currentAdoptionAttempt?.certificate?.original && !currentAdoptionAttempt?.certificate?.signed
        ? { label: 'À compléter', color: '#F8E9C7' }
        : { label: 'Ajouté', color: '#DDEFE5' }
    }
    if (!currentAdoptionAttempt?.certificate?.original && !currentAdoptionAttempt?.certificate?.signed) {
      return { label: 'En attente', color: '#DCEAF8' }
    }
    if (!currentAdoptionAttempt?.certificate?.signed) {
      return {
        label: currentAdoptionAttempt?.certificate?.deliveredByHand ? 'Remis en main propre' : 'En attente',
        color: currentAdoptionAttempt?.certificate?.deliveredByHand ? '#EEF2F7' : '#DCEAF8',
      }
    }
    if (!currentAdoptionAttempt?.certificate?.validated) {
      return { label: 'À valider', color: '#F8E9C7' }
    }
    return {
      label: currentAdoptionAttempt?.certificate?.deliveredByHand ? 'Remis en main propre' : 'Signé',
      color: currentAdoptionAttempt?.certificate?.deliveredByHand ? '#EEF2F7' : '#DDEFE5',
    }
  })()

  const contractSummary = (() => {
    if (isPastAdoptionOutsidePetso) {
      return !currentAdoptionAttempt?.contract?.original && !currentAdoptionAttempt?.contract?.signed
        ? { label: 'À compléter', color: '#F8E9C7' }
        : { label: 'Reçu - hors Petso', color: '#DDEFE5' }
    }
    if (isPetsoProcedureWithManualDocuments) {
      return !currentAdoptionAttempt?.contract?.original && !currentAdoptionAttempt?.contract?.signed
        ? { label: 'À compléter', color: '#F8E9C7' }
        : { label: 'Ajouté', color: '#DDEFE5' }
    }
    if (!currentAdoptionAttempt?.contract?.original) {
      return { label: 'En attente', color: '#DCEAF8' }
    }
    if (!currentAdoptionAttempt?.contract?.signed) {
      return {
        label: currentAdoptionAttempt?.contract?.deliveredByHand
          ? 'Remis en main propre'
          : currentAdoptionAttempt?.contract?.isLocked
          ? 'Prêt pour envoi'
          : 'En attente',
        color: currentAdoptionAttempt?.contract?.deliveredByHand ? '#EEF2F7' : '#DCEAF8',
      }
    }
    if (!currentAdoptionAttempt?.contract?.validated) {
      return { label: 'À valider', color: '#F8E9C7' }
    }
    return {
      label: currentAdoptionAttempt?.contract?.deliveredByHand ? 'Remis en main propre' : 'Signé',
      color: currentAdoptionAttempt?.contract?.deliveredByHand ? '#EEF2F7' : '#DDEFE5',
    }
  })()

  const depositSummary =
    !hasDeposit || (!depositAmount && !depositDate)
      ? { label: 'Non demandé', color: '#EEF2F7' }
      : isPastAdoptionOutsidePetso
      ? {
          label:
            depositStatus === 'true'
              ? `${depositAmount || 0}€ reçu - hors Petso`
              : `${depositAmount || 0}€ à compléter`,
          color: depositStatus === 'true' ? '#DDEFE5' : '#F8E9C7',
        }
      : {
          label: `${depositAmount || 0}€ ${depositStatus === 'true' ? 'reçu' : 'en attente'}`,
          color: depositStatus === 'true' ? '#DDEFE5' : '#FFF1CC',
        }

  const paymentSummary = (() => {
    if (isDonation) {
      return { label: 'Don', color: '#EEF2F7' }
    }

    if (isPastAdoptionOutsidePetso) {
      if (
        watchedOfferType == null ||
        (!isFreeAmount && (normalizedAdoptionPrice == null || normalizedAdoptionPrice === 0))
      ) {
        return { label: 'À compléter', color: '#F8E9C7' }
      }

      if (paymentDone) {
        return {
          label: `${getAdoptionAmountDisplay(
            watchedOfferType,
            normalizedAdoptionPrice,
            paymentAmountInEuros
          )} reçu - hors Petso`,
          color: '#DDEFE5',
        }
      }

      if (hasDeposit && depositStatus === 'true') {
        return {
          label: `${getAdoptionAmountDisplay(watchedOfferType, normalizedAdoptionPrice)} acompte reçu`,
          color: '#DCEAF8',
        }
      }

      return {
        label: `${getAdoptionAmountDisplay(watchedOfferType, normalizedAdoptionPrice)} en attente`,
        color: '#FFF1CC',
      }
    }

    if (paymentDone) {
      return {
        label: `${getAdoptionAmountDisplay(watchedOfferType, normalizedAdoptionPrice, paymentAmountInEuros)} validé`,
        color: '#DDEFE5',
      }
    }

    if (hasDeposit && depositStatus === 'true') {
      return {
        label: `${getAdoptionAmountDisplay(watchedOfferType, normalizedAdoptionPrice)} acompte reçu`,
        color: '#DCEAF8',
      }
    }

    return {
      label: `${getAdoptionAmountDisplay(watchedOfferType, normalizedAdoptionPrice)} en attente`,
      color: '#FFF1CC',
    }
  })()

  const sterilizationSummary = (() => {
    if (isAlreadySterilizedByAnimal) {
      return { label: 'Non requise', color: '#EEF2F7' }
    }

    if (currentAdoptionAttempt?.sterilization?.needed === true) {
      return { label: 'Demandée', color: '#DCEAF8' }
    }

    if (currentAdoptionAttempt?.sterilization?.needed === false) {
      return { label: 'Non demandée', color: '#EEF2F7' }
    }

    return { label: 'À compléter', color: '#F8E9C7' }
  })()

  const shouldShowSterilizationSummary =
    isPastAdoptionOutsidePetso ||
    isAlreadySterilizedByAnimal ||
    currentAdoptionAttempt?.sterilization?.needed === true ||
    currentAdoptionAttempt?.sterilization?.needed === false

  const procedureSummaryItems = [
    { title: 'Certificat', summary: certificateSummary },
    { title: 'Contrat', summary: contractSummary },
    { title: 'Acompte', summary: depositSummary },
    { title: "Frais d'adoption", summary: paymentSummary },
    ...(shouldShowSterilizationSummary ? [{ title: 'Stérilisation', summary: sterilizationSummary }] : []),
  ]

  const canCreateAdoptions =
    accountsStore.isAdmin() === true || accountsStore.connectedCustomer?.editorsCanCreateAdoptions !== false

  const adoptionPrimaryAction = (() => {
    if (watchAdoptionStatus === AnimalAdoptionDtoStatus.Adoptable) {
      if (isDirty) {
        return (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
            <Button
              variant="outlined"
              type="submit"
              disabled={editAnimalMutation.isLoading || !isDirty}
              sx={{
                minHeight: 40,
                width: { xs: '100%', sm: 'auto' },
                textTransform: 'none',
                borderRadius: '20px',
                px: 2.25,
                py: 0.9,
                fontWeight: 700,
                color: colors.cello,
                borderColor: 'rgba(38, 62, 120, 0.22)',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: colors.cello,
                  backgroundColor: 'rgba(38, 62, 120, 0.04)',
                  boxShadow: 'none',
                },
              }}
            >
              Mettre à jour
            </Button>
          </Box>
        )
      }

      return (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: { xs: 'stretch', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            onClick={() =>
              setValue('adoptionStatus', AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable, {
                shouldDirty: true,
              })
            }
            sx={{
              minHeight: 40,
              width: { xs: '100%', sm: 'auto' },
              textTransform: 'none',
              borderRadius: '20px',
              px: 2.25,
              py: 0.9,
              fontWeight: 700,
              color: colors.cello,
              borderColor: 'rgba(38, 62, 120, 0.22)',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': {
                borderColor: colors.cello,
                backgroundColor: 'rgba(38, 62, 120, 0.04)',
                boxShadow: 'none',
              },
            }}
          >
            Démarrer une adoption &gt;
          </Button>
        </Box>
      )
    }

    if (!cannotBeAdopted) {
      return (
        <Tooltip title={canCreateAdoptions ? '' : `L'administrateur ne vous a pas autorisé cet accès`}>
          <span>
            <ControlledAutoCompleteWithCustomValue
              control={control}
              fieldName={'adopterId'}
              defaultValue=""
              size="small"
              options={[
                { value: 'addAccount', label: 'Ajouter un adoptant' },
                ...options
                  .sort((a, b) => a.label.localeCompare(b.label))
                  .map((option) => ({
                    label: option.label,
                    value: option.id,
                  })),
              ]}
              label="Adoptant"
              error={undefined}
              requiredRule={undefined}
              highlight
              disabled={!canCreateAdoptions}
            />
          </span>
        </Tooltip>
      )
    }

    return (
      <ColoredChipSelectField
        control={control}
        error={errors.cannotBeAdoptedStatus}
        fieldName="cannotBeAdoptedStatus"
        label="Motif non adoptable:"
        options={Object.values(AnimalAdoptionDtoCannotBeAdoptedStatus).map(
          (status: AnimalAdoptionDtoCannotBeAdoptedStatus) => ({
            label: getReadableCannotBeAdoptedStatus(status),
            value: status,
            color: getColorForCannotBeAdoptedStatus(status),
          })
        )}
        requiredRule={undefined}
        size="small"
      />
    )
  })()

  return (
    <Box display="flex" flexDirection="row" flexWrap="nowrap" sx={{ ...spacingItem, backgroundColor: colors.white }}>
      <FastLaunchProcedureDialog
        open={adoptionPopUpOpen}
        setOpen={setAdoptionPopUpOpen}
        adopter={selectedAdopter}
        providedAnimal={animal}
        attemptId={attemptId}
        initialAdoptionAttempt={dialogAttempt}
        adoptionContext={dialogAdoptionContext}
        noAdopterDelete
        sendEmailToAdopter={sendEmailToAdopter}
      />
      {guestAdoptionLink && (
        <GuestAdoptionLinkDialog
          link={guestAdoptionLink}
          collectedDeposit={dialogAdoptionContext?.depositStatus ? dialogAdoptionContext?.depositAmount : undefined}
          onDone={() => {
            const adopterId = guestAdoptionLink.adopterId
            const createdAttemptId = guestAdoptionLink.attemptId
            setGuestAdoptionLink(null)
            navigate(`${PATHS.suiviAdoptant.absolute}/${adopterId}?tab=ongoing-adoption&attemptId=${createdAttemptId}`)
          }}
        />
      )}
      <Backdrop
        open={isRestoringAdoptionFlow}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 2,
          backgroundColor: 'rgba(32, 56, 100, 0.24)',
        }}
      >
        <Box
          sx={{
            width: { xs: 'calc(100vw - 32px)', sm: 460 },
            maxWidth: 'calc(100vw - 32px)',
            borderRadius: 3,
            backgroundColor: colors.white,
            boxShadow: '0 18px 50px rgba(32, 56, 100, 0.18)',
            px: 3,
            py: 3.5,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <CircularProgress sx={{ color: colors.amber, mb: 2 }} />
          <Typography sx={{ color: colors.cello, fontSize: '1.1rem', fontWeight: 800, mb: 0.75 }}>
            Ajout de l&apos;adoptant en cours
          </Typography>
          <Typography sx={{ color: '#7A8197', fontSize: '0.95rem', lineHeight: 1.45 }}>
            Lancement de la procédure d&apos;adoption, veuillez patienter quelques instants.
          </Typography>
        </Box>
      </Backdrop>
      <Card sx={{ width: '100%' }}>
        <form onSubmit={handleSubmit((data) => onSubmit(data))}>
          <CardHeader title="🐶 Adoption" />
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={3}>
                <ColoredChipSelectField
                  label="Statut"
                  control={control}
                  error={errors.adoptionStatus}
                  fieldName="adoptionStatus"
                  chipTone="important"
                  options={Object.values(AnimalAdoptionDtoStatus)
                    .filter((status) => status !== AnimalAdoptionDtoStatus['ToBeFilled'])
                    .map((status: AnimalAdoptionDtoStatus) => ({
                      label: getReadableAnimalAdoptionStatus(status),
                      value: status,
                      color: getColorForAnimalAdoptionStatus(status),
                    }))}
                  requiredRule="Ce champs est requis"
                  size="small"
                />
              </Grid>

              {watchAdoptionStatus !== AnimalAdoptionDtoStatus.CannotBeAdopted &&
                (
                  [
                    AnimalAdoptionDtoStatus.HasBeenAdopted,
                    AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable,
                    AnimalAdoptionDtoStatus.Adoptable,
                  ] as AnimalAdoptionDtoStatus[]
                ).includes(watchAdoptionStatus) && (
                  <>
                    <Grid item xs={12} sm={3}>
                      <ControlledSelectField
                        control={control}
                        error={errors.offerType}
                        fieldName="offerType"
                        label="Nature de l'offre"
                        options={animalAdoptionOfferTypeOrder.map((field: AnimalAdoptionDtoOfferType) => ({
                          label: getReadableAnimalAdoptionOfferType(field),
                          value: field,
                        }))}
                        requiredRule=""
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        id="price-input"
                        label={isFreeAmount ? 'Montant minimum (min. 1€)' : 'Montant'}
                        type="number"
                        inputProps={{
                          step: '0.01',
                          inputMode: 'decimal',
                          pattern: '[0-9]*',
                          min: isFreeAmount ? 1 : 0,
                        }}
                        disabled={isDonation}
                        fullWidth
                        {...register('priceInEuros', {
                          validate: (value) => {
                            if (!isFreeAmount) return true
                            const normalized = Number(value)
                            return normalized >= 1 || 'Le montant minimum doit être au moins de 1€'
                          },
                        })}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                  </>
                )}

              <Grid item xs={12} sm={3}>
                {adoptionPrimaryAction}
              </Grid>
              {watchAdoptionStatus === AnimalAdoptionDtoStatus.HasBeenAdopted && (
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={watch('deceasedAfterAdoption')}
                        onChange={(e) =>
                          setValue('deceasedAfterAdoption', e.target.checked, {
                            shouldDirty: true,
                          })
                        }
                      />
                    }
                    label="Animal décédé post adoption"
                  />
                </Grid>
              )}

              {hasExitDates && (
                <Box sx={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
                  <AnimalCardSaveButton isLoading={editAnimalMutation.isLoading} disabled={!isDirty} />
                </Box>
              )}

              {/* Hidden to redirect users to new adoption system */}
              {/* {(
                [
                  AnimalAdoptionDtoStatus.HasBeenAdopted,
                  AnimalAdoptionDtoStatus.InTheProcessOfBeingAdoptable,
                ] as Array<string>
              ).includes(watchAdoptionStatus) && <AdoptionAdministrationFollowUpList animal={animal} />} */}

              {watchAdoptionStatus === AnimalAdoptionDtoStatus.CannotBeAdopted &&
                watchCannotBeAdoptedStatus === AnimalAdoptionDtoCannotBeAdoptedStatus.LongTermWithHostedFamily && (
                  <Box sx={{ display: 'flex', justifyContent: 'left', marginLeft: 3 }}>
                    <Typography variant="h6" fontWeight={400}>
                      FALD: les coordonnées retenues sont celles de la famille en charge actuellement
                    </Typography>
                  </Box>
                )}

              {watchAdoptionStatus !== AnimalAdoptionDtoStatus.CannotBeAdopted &&
                watchAdoptionStatus !== AnimalAdoptionDtoStatus.Adoptable &&
                hasLinkedAdoptionProcedure &&
                currentAdoptionAttempt &&
                watchAdopter && (
                  <Grid item xs={12} sm={12}>
                    <Typography variant="h4" sx={{ fontSize: 20, fontWeight: 700 }}>
                      Procédure d&apos;adoption
                    </Typography>
                    <Box
                      sx={{
                        mt: 1.5,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.25,
                        alignItems: 'center',
                      }}
                    >
                      {procedureSummaryItems.map((item) => (
                        <Box
                          key={item.title}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.75,
                            px: 1.1,
                            py: 0.7,
                            borderRadius: 999,
                            border: '1px solid rgba(122, 129, 151, 0.18)',
                            backgroundColor: '#fff',
                          }}
                        >
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.cello }}>
                            {item.title}
                          </Typography>
                          <Chip label={item.summary.label} sx={getSoftChipSx(item.summary.color)} />
                        </Box>
                      ))}
                    </Box>
                    {isPastAdoptionOutsidePetso && (
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          borderRadius: 3,
                          border: '1px solid rgba(82, 160, 255, 0.22)',
                          backgroundColor: '#EFF6FF',
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: 1,
                        }}
                      >
                        <Info sx={{ color: colors.cello, mt: '1px' }} fontSize="small" />
                        <Typography sx={{ fontSize: 13.5, lineHeight: 1.45, color: colors.cello }}>
                          Cette adoption a été réalisée sans la procédure en ligne Petso. Pensez à compléter
                          manuellement le statut des paiements et la réception des documents.
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
                      <Button
                        component={Link}
                        to={`${PATHS.suiviAdoptant.absolute}/${watchAdopter}/?tab=${adoptionAttemptTab}&attemptId=${
                          currentAdoptionAttempt?.id || ''
                        }`}
                        variant="outlined"
                        sx={{
                          textTransform: 'none',
                          borderRadius: 999,
                          px: 2.25,
                          py: 0.9,
                          fontWeight: 700,
                          color: colors.cello,
                          borderColor: 'rgba(38, 62, 120, 0.22)',
                          backgroundColor: 'transparent',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: colors.cello,
                            backgroundColor: 'rgba(38, 62, 120, 0.04)',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        Accéder à la procédure &gt;
                      </Button>
                    </Box>
                  </Grid>
                )}

              {false && hasLinkedAdoptionProcedure && currentAdoptionAttempt && (
                <Grid item xs={12} sm={12}>
                  <Typography variant="h4" sx={{ fontSize: 20, fontWeight: 700 }}>
                    Acompte & réglement
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.cello,
                        fontSize: { xs: '13px', sm: '13.5px' },
                        fontStyle: 'italic',
                      }}
                    >
                      Un acompte doit-il être versé ?
                    </Typography>
                    <Tooltip title="Si un acompte est prévu, il reste géré hors paiement en ligne Petso. L'adoptant pourra payer en ligne uniquement le reste à charge, ou la totalité tant que l'acompte n'a pas été validé.">
                      <IconButton size="small" sx={{ color: colors.cello, p: 0.35 }}>
                        <Info sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                    <Switch
                      checked={hasDeposit}
                      onChange={(e) =>
                        setValue('hasDeposit', e.target.checked, {
                          shouldDirty: true,
                        })
                      }
                      color="primary"
                      size="small"
                    />
                  </Box>
                  {fullPaymentCanBeHandledInProcedure && currentAdoptionAttempt && watchAdopter && (
                    <Box
                      sx={{
                        mt: 1.5,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 2,
                        backgroundColor: '#EEF4FF',
                        border: '1px solid #C9D8F2',
                      }}
                    >
                      <Typography
                        sx={{
                          color: colors.cello,
                          fontSize: { xs: '13px', sm: '13.5px' },
                          fontStyle: 'italic',
                        }}
                      >
                        En utilisant la{' '}
                        <Link
                          to={`${PATHS.suiviAdoptant.absolute}/${watchAdopter}/?tab=${adoptionAttemptTab}&attemptId=${
                            currentAdoptionAttempt?.id || ''
                          }`}
                          style={{ color: colors.cello, fontWeight: 700 }}
                        >
                          procédure d&apos;adoption Petso
                        </Link>
                        , l&apos;adoptant pourra régler les frais d&apos;adoption restants directement depuis son
                        espace. Vous serez notifié de la réception via la procédure.
                      </Typography>
                    </Box>
                  )}
                  {hiddenDepositExists && (
                    <Box
                      sx={{
                        mt: 1.5,
                        px: 1.5,
                        py: 1.25,
                        borderRadius: 2,
                        backgroundColor: '#FFF6E4',
                        border: '1px solid #F1D19A',
                      }}
                    >
                      <Typography
                        sx={{
                          color: colors.cello,
                          fontSize: { xs: '13px', sm: '13.5px' },
                          fontStyle: 'italic',
                        }}
                      >
                        Si le montant indiqué est inférieur au montant de l&apos;adoption, activez le switch : un
                        montant d&apos;acompte a peut-être déjà été renseigné.
                      </Typography>
                    </Box>
                  )}
                  <Grid container spacing={1}>
                    {hasDeposit && (
                      <>
                        <Grid item xs={12} sm={4}>
                          <ControlledDateField
                            control={control}
                            error={errors.depositDate}
                            fieldName="depositDate"
                            label="Date de l'acompte"
                            requiredRule=""
                            size="small"
                            sx={{ marginTop: 2 }}
                            validate={(value) => isValidPastDateCoherence(value) || value === null}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            id="price-input"
                            label="Montant de l'acompte"
                            type="number"
                            inputProps={{
                              step: '0.01',
                              inputMode: 'decimal',
                              pattern: '[0-9]*',
                            }}
                            disabled={isDonation}
                            fullWidth
                            {...register('depositAmount')}
                            InputProps={{
                              endAdornment: <InputAdornment position="end">€</InputAdornment>,
                            }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ marginTop: 2 }}
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <ColoredChipSelectField
                            control={control}
                            error={errors.depositStatus}
                            fieldName="depositStatus"
                            label="État de l'acompte"
                            options={[
                              { label: 'Validé', value: 'true', color: colors.seaGreen },
                              { label: 'En attente', value: 'false', color: colors.amber },
                            ]}
                            requiredRule=""
                            sx={{ marginTop: 2 }}
                            size="small"
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={4}>
                      <TextField
                        id="price-input"
                        label={isFreeAmount ? 'Montant min. restant à payer' : 'Montant restant à payer'}
                        value={`${leftToPay}€`}
                        fullWidth
                        inputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ marginTop: 2, cursor: 'disabled', input: { cursor: 'not-allowed' } }}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ColoredChipSelectField
                        control={control}
                        error={undefined}
                        fieldName={'paymentDone'}
                        label="État du paiement"
                        options={[
                          { label: 'Validé', value: 'true', color: colors.seaGreen },
                          { label: 'En attente', value: 'false', color: colors.amber },
                        ]}
                        requiredRule=""
                        sx={{ marginTop: 2 }}
                        size="small"
                        readOnly
                      />
                    </Grid>
                    {paymentDone && (
                      <Grid item xs={12} sm={4}>
                        <TextField
                          id="payment-date-input"
                          label="Date du paiement"
                          value={paymentDateDisplay}
                          fullWidth
                          inputProps={{ readOnly: true }}
                          InputLabelProps={{ shrink: true }}
                          sx={{ marginTop: 2, cursor: 'disabled', input: { cursor: 'not-allowed' } }}
                          size="small"
                        />
                      </Grid>
                    )}
                    {!paymentDone && <Grid item xs={12} sm={4} />}
                  </Grid>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <AnimalCardSaveButton
                      isLoading={editAnimalMutation.isLoading}
                      disabled={!isDirty}
                      fullWidthOnMobile={false}
                    />
                  </Box>
                </Grid>
              )}

              {hasExitDates && (
                <Grid item xs={12} sm={12}>
                  <Typography variant="h4" sx={{ fontSize: 20, fontWeight: 700 }}>
                    Infos de sortie
                  </Typography>
                  <Box
                    sx={{
                      marginLeft: 2,
                      borderLeft: '2px grey solid',
                      borderLeftColor: 'grey.200',
                      marginTop: isAdoptedStatus ? 2 : 1,
                      paddingLeft: 2,
                      paddingTop: isAdoptedStatus ? 1.5 : 0.5,
                      paddingBottom: isAdoptedStatus ? 1 : 0,
                      boxSizing: 'border-box',
                      paddingRight: 3,
                    }}
                  >
                    <Grid container spacing={1.5} sx={{ marginTop: 0.5 }}>
                      <Grid item xs={12} sm={3}>
                        <ControlledDateField
                          control={control}
                          validate={(value) => isValidPastDateCoherence(value) || value === null}
                          error={undefined}
                          fieldName={'futureAdoptionDate'}
                          label="Date d'adoption prévue"
                          requiredRule={undefined}
                          sx={{ marginTop: 0 }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={3}>
                        <ControlledDateField
                          control={control}
                          validate={(value) => isValidPastDateCoherence(value) || value === null}
                          // error={errors.adoptionDate}
                          error={undefined}
                          fieldName={'adoptionDate'}
                          label="Adoption (physique)"
                          requiredRule={undefined}
                          sx={{ marginTop: 0 }}
                          size="small"
                          maxDate={dayjs()}
                        />
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <Tooltip title={exitDatesDisabled ? "Veuillez d'abord remplir la date d'adoption" : ''}>
                          <span>
                            <ControlledDateField
                              control={control}
                              validate={(value) => isValidPastDateCoherence(value) || value === null}
                              // error={errors.adoptionDate}
                              error={undefined}
                              fieldName={'vpaDate'}
                              label="Visite post-adoption"
                              requiredRule={undefined}
                              sx={{ marginTop: 0 }}
                              size="small"
                              minDate={dayjs(adoptionDate, 'DD/MM/YYYY')}
                              disabled={exitDatesDisabled}
                            />
                          </span>
                        </Tooltip>
                      </Grid>

                      <Grid item xs={12} sm={3}>
                        <Tooltip title={exitDatesDisabled ? "Veuillez d'abord remplir la date d'adoption" : ''}>
                          <span>
                            <ControlledDateField
                              control={control}
                              validate={(value) => isValidPastDateCoherence(value) || value === null}
                              error={errors.exitDate}
                              fieldName={'exitDate'}
                              label="Date de sortie (Cession I-CAD)"
                              requiredRule={undefined}
                              sx={{ marginTop: 0 }}
                              size="small"
                              minDate={dayjs(adoptionDate, 'DD/MM/YYYY')}
                              disabled={exitDatesDisabled}
                            />
                          </span>
                        </Tooltip>
                      </Grid>

                      {hasExitDates && (
                        <Grid item xs={12} sm={3}>
                          <TextField
                            id="new-name-input"
                            label="Nouveau nom"
                            type="text"
                            fullWidth
                            {...register('newName')}
                            size="small"
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>
              )}

              {/* GOODBYE */}
              {hasGoodbyeDates && (
                <Grid item xs={12} sm={12} sx={spacingItem}>
                  <Box
                    sx={{
                      marginLeft: 2,
                      borderLeft: '2px grey solid',
                      borderLeftColor: 'grey.200',
                      marginTop: 1,
                      paddingLeft: 2,
                      boxSizing: 'border-box',
                      paddingRight: 3,
                    }}
                  >
                    <Typography variant="h6">
                      {map[watchCannotBeAdoptedStatus as GoodbyeCannotBeAdoptedStatus].title}
                    </Typography>

                    <Grid container spacing={1} sx={{ marginTop: 1 }}>
                      <Grid item xs={12} sm={animal.adoption.cannotBeAdoptedStatus === 'released' ? 3 : 6}>
                        <ControlledDateField
                          control={control}
                          validate={(value) => isValidPastDateCoherence(value) || value === null}
                          error={errors.goodbyeDate}
                          fieldName={'goodbyeDate'}
                          label={map[watchCannotBeAdoptedStatus as GoodbyeCannotBeAdoptedStatus].date}
                          requiredRule={undefined}
                          size="small"
                          sx={spacingItem}
                        />
                      </Grid>

                      <Grid item xs={12} sm={animal.adoption.cannotBeAdoptedStatus === 'released' ? 3 : 6}>
                        <TextField
                          label={map[watchCannotBeAdoptedStatus as GoodbyeCannotBeAdoptedStatus].details}
                          type="text"
                          fullWidth
                          sx={{ marginTop: 2 }}
                          {...register('goodbyeDetails')}
                          multiline
                          size="small"
                        />
                      </Grid>

                      {animal.adoption.status === 'cannot-be-adopted' &&
                        animal.adoption.cannotBeAdoptedStatus === 'released' && (
                          <Grid item xs={12} sm={3}>
                            <TextField
                              label="Coordonnées GPS"
                              type="text"
                              fullWidth
                              {...register('gpsPoint')}
                              error={!!errors.gpsPoint}
                              helperText={`Vous pouvez copier-coller les coordonnées depuis Google maps`}
                              sx={spacingItem}
                            />
                          </Grid>
                        )}
                    </Grid>
                  </Box>
                </Grid>
              )}

              {/* GARDE SOCIALE */}
              {hasSocialSecurityDates && (
                <Grid item xs={12} sm={12} sx={spacingItem}>
                  <Box
                    sx={{
                      marginLeft: 2,
                      borderLeft: '2px grey solid',
                      borderLeftColor: 'grey.200',
                      marginTop: 1,
                      paddingLeft: 2,
                      boxSizing: 'border-box',
                      paddingRight: 3,
                    }}
                  >
                    <Typography variant="h6">Garde sociale</Typography>

                    <Grid container spacing={1} sx={{ marginTop: 1 }}>
                      <Grid item xs={12} sm={6}>
                        <ControlledDateField
                          control={control}
                          validate={(value) => isValidPastDateCoherence(value) || value === null}
                          error={errors.socialSecurityEntryDate}
                          fieldName={'socialSecurityEntryDate'}
                          label="Date d'entrée"
                          requiredRule={undefined}
                          size="small"
                          sx={spacingItem}
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <ControlledDateField
                          control={control}
                          validate={(value) => isValidPastDateCoherence(value) || value === null}
                          error={errors.socialSecurityExitDate}
                          fieldName={'socialSecurityExitDate'}
                          label="Date de sortie"
                          requiredRule={undefined}
                          size="small"
                          sx={spacingItem}
                          minDate={
                            watch('socialSecurityEntryDate') && dayjs(watch('socialSecurityEntryDate')).isValid()
                              ? dayjs(watch('socialSecurityEntryDate'))
                              : undefined
                          }
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              )}
            </Grid>
          </CardContent>
          {watchAdoptionStatus !== AnimalAdoptionDtoStatus.Adoptable && (
            <CardActions sx={{ justifyContent: 'end' }}>
              <AnimalCardSaveButton isLoading={editAnimalMutation.isLoading} disabled={!isDirty} />
            </CardActions>
          )}
        </form>
      </Card>
      <CreateAdoptionAttemptConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        alreadyExistingAttempt={alreadyExistingAttempt}
        oldAdopterName={oldAdopterName}
      />
      <ForceAdopterAcceptedModal
        isOpen={adoptedModalIsOpen}
        onClose={onCloseAdoptedModal}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        alreadyExistingAttempt={alreadyExistingAttempt}
        isSameAdopter={isSameAdopter}
        oldAdopterName={oldAdopterName}
      />
      <SuspendAttemptModal
        isOpen={suspendModalIsOpen}
        onClose={onCloseSuspendModal}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        oldAdopterName={oldAdopterName}
      />
      <GetAnimalAdoptedModal
        isOpen={getAnimalAdoptedIsOpen}
        onClose={onCloseGetAnimalAdopted}
        handleSubmit={handleSubmit}
        onSubmit={onSubmit}
        animalName={animal.name}
      />
      {initialAdopter && (
        <VerdictAfterVPAModal
          isOpen={verdictVPAModalIsOpen}
          onClose={onCloseVerdictVPAModal}
          animal={animal}
          adopter={initialAdopter}
        />
      )}
    </Box>
  )
}

type GoodbyeCannotBeAdoptedStatus =
  | typeof AnimalAdoptionDtoCannotBeAdoptedStatus.Dead
  | typeof AnimalAdoptionDtoCannotBeAdoptedStatus.Lost
  | typeof AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation
  | typeof AnimalAdoptionDtoCannotBeAdoptedStatus.Released

const map = {
  [AnimalAdoptionDtoCannotBeAdoptedStatus.Dead]: {
    title: 'Décès',
    date: 'Date du décès',
    details: 'Cause du décès',
  },
  [AnimalAdoptionDtoCannotBeAdoptedStatus.Lost]: {
    title: 'Perte',
    date: 'Date de perte',
    details: 'Détails',
  },
  [AnimalAdoptionDtoCannotBeAdoptedStatus.TransferToAnotherAssociation]: {
    title: 'Transfert',
    date: 'Date du transfert',
    details: "Coordonnées de l'association",
  },
  [AnimalAdoptionDtoCannotBeAdoptedStatus.Released]: {
    title: 'Sortie',
    date: 'Date de sortie',
    details: 'Lieu de stationnement',
  },
}
